import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SkeletonTemplates } from "../components/SkeletonLoader";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import FadeIn from "../components/FadeIn";
import { FileText, RefreshCw, X, Info, Upload, Plus, Edit2, Trash2 } from 'lucide-react';
import TemplateVersionsModal from '../components/TemplateVersionsModal';
import ConfirmModal from '../components/ConfirmModal';
import { useToast, ToastContainer } from '../components/Toast';
import { MACHINE_TYPES, MACHINE_TYPE_COLORS } from '../constants';

const Templates = () => {
  const { toasts, removeToast, success, error: toastError, warning: toastWarning } = useToast();

  const [user, setUser] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const showLoading = useDelayedLoading(loading, 250);

  const [showVersionsFor, setShowVersionsFor] = useState(null);
  const [uploadForm, setUploadForm] = useState({ file: null });
  const [confirmAction, setConfirmAction] = useState(null);

  // Upload new template
  const [showNewUpload, setShowNewUpload] = useState(false);
  const [newUploadForm, setNewUploadForm] = useState({
    file: null, name: '', description: '', machine_types: MACHINE_TYPES.map(t => t.value)
  });
  const [uploading, setUploading] = useState(false);

  // Edit template modal
  const [showEditFor, setShowEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', machine_types: [] });
  const [saving, setSaving] = useState(false);

  // Update version modal
  const [showUpdateFor, setShowUpdateFor] = useState(null);

  // Filter
  const [machineTypeFilter, setMachineTypeFilter] = useState('all');

  // Lock body scroll when any modal is open
  useEffect(() => {
    const anyOpen = showNewUpload || !!showEditFor || !!showUpdateFor;
    document.body.style.overflow = anyOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showNewUpload, showEditFor, showUpdateFor]);

  useEffect(() => {
    fetchTemplates();
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setUser(json.user || json.data);
      }
    } catch (e) {
      console.error('Auth error:', e);
    }
  };

  const fetchTemplates = async (filter) => {
    const activeFilter = filter !== undefined ? filter : machineTypeFilter;
    try {
      const url = (activeFilter && activeFilter !== 'all')
        ? `/api/templates?machine_type=${activeFilter}`
        : '/api/templates';
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setTemplates(json.data || []);
      }
    } catch (e) {
      console.error('Templates fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filter) => {
    setMachineTypeFilter(filter);
    fetchTemplates(filter);
  };

  // Toggle machine type checkbox helper
  const toggleMachineType = (formSetter, currentTypes, value) => {
    const newTypes = currentTypes.includes(value)
      ? currentTypes.filter(t => t !== value)
      : [...currentTypes, value];
    formSetter(prev => ({ ...prev, machine_types: newTypes }));
  };

  // Open/close upload modal
  const openNewUpload = () => {
    setNewUploadForm({ file: null, name: '', description: '', machine_types: MACHINE_TYPES.map(t => t.value) });
    setShowNewUpload(true);
  };
  const closeNewUpload = () => {
    setShowNewUpload(false);
    setNewUploadForm({ file: null, name: '', description: '', machine_types: MACHINE_TYPES.map(t => t.value) });
  };

  // Open/close edit modal
  const openEditModal = (template) => {
    // Handle both new machine_types array and old model_type string
    let machineTypes = MACHINE_TYPES.map(t => t.value);
    if (template.machine_types && Array.isArray(template.machine_types) && template.machine_types.length > 0) {
      machineTypes = template.machine_types;
    } else if (template.model_type) {
      machineTypes = [template.model_type];
    }
    setEditForm({ name: template.name || '', machine_types: machineTypes });
    setShowEditFor(template);
  };
  const closeEditModal = () => {
    setShowEditFor(null);
    setEditForm({ name: '', machine_types: [] });
  };

  // Upload new template
  const handleNewUpload = async (e) => {
    e.preventDefault();
    if (!newUploadForm.file) return;
    if (newUploadForm.machine_types.length === 0) {
      toastError('Vyberte alespoň jeden typ trenažéru.', 'Chyba validace');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', newUploadForm.file);
      if (newUploadForm.name.trim()) formData.append('name', newUploadForm.name.trim());
      if (newUploadForm.description.trim()) formData.append('description', newUploadForm.description.trim());
      formData.append('machine_types', JSON.stringify(newUploadForm.machine_types));

      const res = await fetch('/api/templates', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (res.ok) {
        closeNewUpload();
        await fetchTemplates();
        success('Šablona byla úspěšně nahrána.', 'Šablona nahrána');
      } else {
        const err = await res.json().catch(() => ({}));
        if (err.details && Array.isArray(err.details)) {
          toastError(err.details.map(d => d.msg || d.message).join(', '), 'Chyba validace');
        } else {
          toastError(err.error || `Chyba ${res.status}`, 'Chyba nahrávání');
        }
      }
    } catch (err) {
      toastError('Nepodařilo se nahrát šablonu.', 'Chyba');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  // Edit template metadata (name + machine_types)
  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!showEditFor) return;
    if (!editForm.name.trim()) {
      toastError('Název šablony je povinný.', 'Chyba validace');
      return;
    }
    if (editForm.machine_types.length === 0) {
      toastError('Vyberte alespoň jeden typ trenažéru.', 'Chyba validace');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`/api/templates/${showEditFor.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editForm.name.trim(),
          machine_types: editForm.machine_types,
        })
      });

      if (res.ok) {
        closeEditModal();
        await fetchTemplates();
        success('Šablona byla úspěšně upravena.', 'Šablona uložena');
      } else {
        const err = await res.json().catch(() => ({}));
        if (err.details && Array.isArray(err.details)) {
          toastError(err.details.map(d => d.msg || d.message).join(', '), 'Chyba validace');
        } else {
          toastError(err.error || `Chyba ${res.status}`, 'Chyba ukládání');
        }
      }
    } catch (err) {
      toastError('Nepodařilo se uložit změny.', 'Chyba');
      console.error('Edit error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Update template version (file upload)
  const handleUpdateVersion = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !showUpdateFor) return;

    setConfirmAction({
      title: 'Aktualizovat šablonu?',
      message: `Opravdu chcete nahrát novou verzi šablony "${showUpdateFor.name}"? Stará verze zůstane zachována pro existující trenažéry.`,
      onConfirm: async () => {
        const formData = new FormData();
        formData.append('file', uploadForm.file);
        formData.append('template_id', showUpdateFor.id);
        const res = await fetch('/api/templates', {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
        if (res.ok) {
          setShowUpdateFor(null);
          setUploadForm({ file: null });
          await fetchTemplates();
          success('Šablona byla aktualizována.', 'Nová verze nahrána');
        } else {
          const err = await res.json().catch(() => ({}));
          toastError(err.error || `Chyba ${res.status}`, 'Chyba aktualizace');
        }
        setConfirmAction(null);
      }
    });
  };

  const canUpload = user?.role === 'admin' || user?.role === 'manager';

  // Delete template (admin only, 1. sk. templates only)
  const handleDeleteTemplate = (template) => {
    setConfirmAction({
      title: 'Smazat šablonu?',
      message: `Opravdu chcete smazat šablonu "${template.name}"? Tato akce je nevratná.`,
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/templates/${template.id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (res.ok) {
            await fetchTemplates();
            success('Šablona byla smazána.', 'Šablona smazána');
          } else {
            const err = await res.json().catch(() => ({}));
            toastError(err.error || `Chyba ${res.status}`, 'Chyba mazání');
          }
        } catch (err) {
          toastError('Nepodařilo se smazat šablonu.', 'Chyba');
          console.error('Delete error:', err);
        }
        setConfirmAction(null);
      }
    });
  };

  // Helpers for template delete rules
  const isManualTemplate = (template) => {
    const n = (template.name + (template.original_name || '')).toLowerCase();
    return n.includes("manuál") || n.includes("manual");
  };
  const is1skTemplate = (template) => {
    const n = (template.name + (template.original_name || '')).toLowerCase();
    return n.includes("1. sk");
  };

  // Display helper: machine_types → "TR3, TR4" or fallback
  const getMachineTypesDisplay = (template) => {
    if (template.machine_types && Array.isArray(template.machine_types) && template.machine_types.length > 0) {
      return template.machine_types;
    }
    if (template.model_type) return [template.model_type];
    return ['—'];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('cs-CZ', {
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
  };

  // Reusable machine types checkboxes fieldset
  const MachineTypesFieldset = ({ currentTypes, formSetter }) => (
    <fieldset
      style={{
        border: '1px solid #E0E0E0', borderRadius: '8px',
        padding: '12px 16px', marginBottom: '16px',
      }}
    >
      <legend style={{ fontSize: '14px', fontWeight: 500, color: '#333', padding: '0 8px' }}>
        Trenažéry *
      </legend>
      {MACHINE_TYPES.map(mt => (
        <label
          key={mt.value}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            cursor: 'pointer', marginBottom: '8px',
            fontSize: '14px', color: '#333',
          }}
        >
          <input
            type="checkbox"
            name="machine_types"
            value={mt.value}
            checked={currentTypes.includes(mt.value)}
            onChange={() => toggleMachineType(formSetter, currentTypes, mt.value)}
            aria-label={`Trenažér ${mt.label}`}
            style={{ width: '16px', height: '16px', accentColor: '#E53935', cursor: 'pointer' }}
          />
          {mt.label}
        </label>
      ))}
      {currentTypes.length === 0 && (
        <p role="alert" style={{ color: '#E53935', fontSize: '12px', margin: '4px 0 0' }}>
          Vyberte alespoň jeden typ trenažéru.
        </p>
      )}
    </fieldset>
  );

  if (showLoading) return <SkeletonTemplates />;

  return (
    <FadeIn show={!showLoading}>
      <div style={{ padding: '24px' }}>

        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', marginBottom: '24px'
        }}>
          <h1 style={{
            fontSize: '24px', fontWeight: 700, color: '#333',
            margin: 0, display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            <FileText color="#E53935" /> Šablony dokumentů
          </h1>
          {canUpload && (
            <button
              onClick={openNewUpload}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 18px',
                backgroundColor: '#E53935', color: '#FFF',
                border: 'none', borderRadius: '8px',
                cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                boxShadow: '0 2px 6px rgba(229,57,53,0.25)'
              }}
            >
              <Plus size={16} /> Nahrát novou šablonu
            </button>
          )}
        </div>

        {/* ── Info banner ── */}
        <div style={{
          backgroundColor: '#E3F2FD', border: '1px solid #90CAF9',
          borderRadius: '8px', padding: '14px 16px', marginBottom: '24px',
        }}>
          <p style={{ color: '#1565C0', fontSize: '14px', margin: '0 0 4px' }}>
            <strong>Verzování:</strong> Při aktualizaci šablony se stará verze zachová pro existující trenažéry.
          </p>
          <p style={{ color: '#1565C0', fontSize: '13px', margin: 0 }}>
            Šablony se nemažou — historické záznamy jsou povinné.
          </p>
        </div>

        {/* ── Filter buttons ── */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[{ key: 'all', label: 'Všechny' }, ...MACHINE_TYPES.map(t => ({ key: t.value, label: t.shortLabel }))].map(f => (
            <button
              key={f.key}
              onClick={() => handleFilterChange(f.key)}
              aria-pressed={machineTypeFilter === f.key}
              style={{
                padding: '7px 18px', borderRadius: '20px', cursor: 'pointer',
                fontSize: '14px', fontWeight: machineTypeFilter === f.key ? 600 : 400,
                border: machineTypeFilter === f.key ? '2px solid #E53935' : '2px solid #E0E0E0',
                backgroundColor: machineTypeFilter === f.key ? '#FFEBEE' : '#FFF',
                color: machineTypeFilter === f.key ? '#E53935' : '#666',
                transition: 'all 0.15s ease',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* ── Empty state ── */}
        {templates.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '60px 24px',
            backgroundColor: '#FAFAFA', border: '2px dashed #E0E0E0',
            borderRadius: '12px', marginBottom: '24px'
          }}>
            <FileText size={48} color="#BDBDBD" style={{ marginBottom: '16px' }} />
            <p style={{ color: '#757575', fontSize: '16px', marginBottom: '8px', fontWeight: 500 }}>
              Žádné šablony
            </p>
            <p style={{ color: '#9E9E9E', fontSize: '14px', marginBottom: '24px' }}>
              {machineTypeFilter !== 'all'
                ? `Žádné šablony pro trenažér ${machineTypeFilter}.`
                : 'Zatím nejsou nahrané žádné šablony dokumentů.'}
            </p>
            {canUpload && machineTypeFilter === 'all' && (
              <button
                onClick={openNewUpload}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '12px 24px',
                  backgroundColor: '#E53935', color: '#FFF',
                  border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontSize: '14px', fontWeight: 600
                }}
              >
                <Upload size={16} /> Nahrát první šablonu
              </button>
            )}
          </div>
        )}

        {/* ── Template table ── */}
        {templates.length > 0 && (
          <div style={{
            backgroundColor: '#FFF', borderRadius: '12px',
            border: '1px solid #E0E0E0', overflow: 'hidden'
          }}>
            <table
              style={{ width: '100%', borderCollapse: 'collapse' }}
              role="table"
              aria-label="Tabulka šablon dokumentů"
            >
              <thead>
                <tr style={{ backgroundColor: '#FAFAFA', borderBottom: '2px solid #E0E0E0' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#666' }}>
                    Název
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#666' }}>
                    Určena pro
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#666', width: '80px' }}>
                    Verze
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#666', width: '120px' }}>
                    Vytvořena
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: '#666' }}>
                    Akce
                  </th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template, idx) => {
                  const machineTypeTags = getMachineTypesDisplay(template);
                  return (
                    <tr
                      key={template.id}
                      style={{ borderBottom: idx < templates.length - 1 ? '1px solid #F0F0F0' : 'none' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFAFA'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                    >
                      {/* Name */}
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <FileText size={18} color="#E53935" style={{ flexShrink: 0 }} />
                          <div>
                            <p style={{ margin: 0, fontWeight: 600, color: '#333', fontSize: '14px' }}>
                              {template.name}
                            </p>
                            {template.original_name && (
                              <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>
                                {template.original_name}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Machine Types */}
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {machineTypeTags.map(mt => (
                            <span
                              key={mt}
                              style={{
                                display: 'inline-block', padding: '2px 10px',
                                backgroundColor:
                                  MACHINE_TYPE_COLORS[mt]?.bg || '#F5F5F5',
                                color: MACHINE_TYPE_COLORS[mt]?.color || '#666',
                                borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                              }}
                            >
                              {mt}
                            </span>
                          ))}
                        </div>
                      </td>

                      {/* Version */}
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{ fontWeight: 600, color: '#1976D2', fontSize: '14px' }}>
                          v{template.current_version || 1}
                        </span>
                      </td>

                      {/* Created */}
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#666' }}>
                        {formatDate(template.created_at)}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{
                          display: 'flex', gap: '6px',
                          justifyContent: 'flex-end', alignItems: 'center',
                          flexWrap: 'wrap'
                        }}>
                          {/* Version history */}
                          <button
                            onClick={() => setShowVersionsFor(template)}
                            title="Zobrazit historii verzí"
                            aria-label={`Verze šablony ${template.name}`}
                            style={{
                              padding: '6px 10px', borderRadius: '6px',
                              border: '1px solid #E3F2FD', backgroundColor: '#E3F2FD',
                              cursor: 'pointer', display: 'flex', alignItems: 'center',
                              gap: '4px', fontSize: '12px', color: '#1976D2', fontWeight: 500
                            }}
                          >
                            <Info size={14} /> Verze
                          </button>

                          {/* Edit */}
                          {canUpload && (
                            <button
                              onClick={() => openEditModal(template)}
                              title="Upravit název a trenažéry"
                              aria-label={`Upravit šablonu ${template.name}`}
                              style={{
                                padding: '6px 10px', borderRadius: '6px',
                                border: '1px solid #E8F5E9', backgroundColor: '#E8F5E9',
                                cursor: 'pointer', display: 'flex', alignItems: 'center',
                                gap: '4px', fontSize: '12px', color: '#2E7D32', fontWeight: 500
                              }}
                            >
                              <Edit2 size={14} /> Upravit
                            </button>
                          )}

                          {/* New version */}
                          {canUpload && (
                            <button
                              onClick={() => { setShowUpdateFor(template); setUploadForm({ file: null }); }}
                              title="Nahrát novou verzi souboru"
                              aria-label={`Nahrát novou verzi šablony ${template.name}`}
                              style={{
                                padding: '6px 10px', borderRadius: '6px',
                                border: '1px solid #FFF3E0', backgroundColor: '#FFF3E0',
                                cursor: 'pointer', display: 'flex', alignItems: 'center',
                                gap: '4px', fontSize: '12px', color: '#E65100', fontWeight: 500
                              }}
                            >
                              <RefreshCw size={14} /> Nová verze
                            </button>
                          )}

                          {/* Delete — conditional */}
                          {!isManualTemplate(template) && (
                            is1skTemplate(template) && user?.role === 'admin' ? (
                              <button
                                onClick={() => handleDeleteTemplate(template)}
                                title="Smazat šablonu"
                                aria-label={`Smazat šablonu ${template.name}`}
                                style={{
                                  padding: '6px 10px', borderRadius: '6px',
                                  border: '1px solid #FFEBEE', backgroundColor: '#FFEBEE',
                                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                                  gap: '4px', fontSize: '12px', color: '#C62828', fontWeight: 500,
                                }}
                              >
                                <Trash2 size={14} /> Smazat
                              </button>
                            ) : (
                              <button
                                disabled
                                title="Šablony se nemažou — historické záznamy jsou povinné"
                                aria-label="Smazání šablony je zakázáno"
                                aria-disabled="true"
                                style={{
                                  padding: '6px 10px', borderRadius: '6px',
                                  border: '1px solid #EEEEEE', backgroundColor: '#F5F5F5',
                                  cursor: 'not-allowed', display: 'flex', alignItems: 'center',
                                  gap: '4px', fontSize: '12px', color: '#BDBDBD', fontWeight: 500,
                                  opacity: 0.55,
                                }}
                              >
                                <Trash2 size={14} /> Smazat
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ══════════════════════════════════════════════
            MODAL: Nahrát novou šablonu
        ══════════════════════════════════════════════ */}
        {showNewUpload && createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="upload-modal-title"
            style={{
              position: 'fixed', inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 9000
            }}
            onClick={(e) => { if (e.target === e.currentTarget) closeNewUpload(); }}
          >
            <div style={{
              backgroundColor: '#FFF', borderRadius: '16px', padding: '24px',
              width: '100%', maxWidth: '480px', margin: '0 16px',
              boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
              maxHeight: '90vh', overflowY: 'auto'
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: '20px'
              }}>
                <h2 id="upload-modal-title" style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>
                  Nahrát novou šablonu
                </h2>
                <button
                  onClick={closeNewUpload}
                  aria-label="Zavřít dialog"
                  style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <X size={20} color="#999" />
                </button>
              </div>

              <form onSubmit={handleNewUpload}>
                {/* Název */}
                <div style={{ marginBottom: '16px' }}>
                  <label
                    htmlFor="upload-name"
                    style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}
                  >
                    Název šablony
                  </label>
                  <input
                    id="upload-name"
                    type="text"
                    placeholder="Ponechte prázdné pro automatické jméno ze souboru"
                    value={newUploadForm.name}
                    onChange={(e) => setNewUploadForm(f => ({ ...f, name: e.target.value }))}
                    style={{
                      width: '100%', padding: '10px',
                      border: '1px solid #E0E0E0', borderRadius: '8px',
                      boxSizing: 'border-box', fontSize: '14px'
                    }}
                  />
                </div>

                {/* Popis */}
                <div style={{ marginBottom: '16px' }}>
                  <label
                    htmlFor="upload-description"
                    style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}
                  >
                    Popis <span style={{ color: '#999', fontWeight: 400 }}>(volitelně)</span>
                  </label>
                  <textarea
                    id="upload-description"
                    placeholder="Popis šablony..."
                    value={newUploadForm.description}
                    onChange={(e) => setNewUploadForm(f => ({ ...f, description: e.target.value }))}
                    rows={3}
                    style={{
                      width: '100%', padding: '10px',
                      border: '1px solid #E0E0E0', borderRadius: '8px',
                      boxSizing: 'border-box', fontSize: '14px', resize: 'vertical'
                    }}
                  />
                </div>

                {/* Trenažéry checkboxes */}
                <MachineTypesFieldset
                  currentTypes={newUploadForm.machine_types}
                  formSetter={setNewUploadForm}
                />

                {/* Soubor */}
                <div style={{ marginBottom: '20px' }}>
                  <label
                    htmlFor="upload-file"
                    style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}
                  >
                    Soubor *
                  </label>
                  <input
                    id="upload-file"
                    type="file"
                    accept=".docx,.pdf,.doc,.xlsx,.csv,.txt,.odt,.pptx,.jpg,.jpeg,.png"
                    onChange={(e) => setNewUploadForm(f => ({ ...f, file: e.target.files[0] }))}
                    required
                    aria-required="true"
                    style={{
                      width: '100%', padding: '10px',
                      border: '1px solid #E0E0E0', borderRadius: '8px',
                      boxSizing: 'border-box', fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={closeNewUpload}
                    style={{
                      flex: 1, padding: '10px',
                      border: '1px solid #E0E0E0', borderRadius: '8px',
                      backgroundColor: '#FFF', cursor: 'pointer',
                      fontSize: '14px', fontWeight: 500
                    }}
                  >
                    Zrušit
                  </button>
                  <button
                    type="submit"
                    disabled={uploading || newUploadForm.machine_types.length === 0}
                    style={{
                      flex: 1, padding: '10px',
                      backgroundColor: (uploading || newUploadForm.machine_types.length === 0) ? '#CCC' : '#E53935',
                      color: '#FFF', border: 'none', borderRadius: '8px',
                      cursor: (uploading || newUploadForm.machine_types.length === 0) ? 'not-allowed' : 'pointer',
                      fontSize: '14px', fontWeight: 600
                    }}
                  >
                    {uploading ? 'Nahrávám...' : 'Nahrát šablonu'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* ══════════════════════════════════════════════
            MODAL: Upravit šablonu (název + trenažéry)
        ══════════════════════════════════════════════ */}
        {showEditFor && createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-modal-title"
            style={{
              position: 'fixed', inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 9000
            }}
            onClick={(e) => { if (e.target === e.currentTarget) closeEditModal(); }}
          >
            <div style={{
              backgroundColor: '#FFF', borderRadius: '16px', padding: '24px',
              width: '100%', maxWidth: '440px', margin: '0 16px',
              boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
              maxHeight: '90vh', overflowY: 'auto'
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: '20px'
              }}>
                <h2 id="edit-modal-title" style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>
                  Upravit šablonu
                </h2>
                <button
                  onClick={closeEditModal}
                  aria-label="Zavřít dialog"
                  style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <X size={20} color="#999" />
                </button>
              </div>

              <form onSubmit={handleEditSave}>
                {/* Název */}
                <div style={{ marginBottom: '16px' }}>
                  <label
                    htmlFor="edit-name"
                    style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}
                  >
                    Název šablony *
                  </label>
                  <input
                    id="edit-name"
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                    required
                    aria-required="true"
                    style={{
                      width: '100%', padding: '10px',
                      border: '1px solid #E0E0E0', borderRadius: '8px',
                      boxSizing: 'border-box', fontSize: '14px'
                    }}
                  />
                </div>

                {/* Trenažéry checkboxes */}
                <MachineTypesFieldset
                  currentTypes={editForm.machine_types}
                  formSetter={setEditForm}
                />

                {/* Delete — conditional */}
                {showEditFor && !isManualTemplate(showEditFor) && (
                  <div style={{
                    borderTop: '1px solid #F0F0F0', paddingTop: '16px',
                    marginTop: '4px', marginBottom: '16px'
                  }}>
                    {is1skTemplate(showEditFor) && user?.role === 'admin' ? (
                      <button
                        type="button"
                        onClick={() => { closeEditModal(); handleDeleteTemplate(showEditFor); }}
                        title="Smazat šablonu"
                        style={{
                          width: '100%', padding: '10px',
                          backgroundColor: '#FFEBEE', color: '#C62828',
                          border: '1px solid #FFCDD2', borderRadius: '8px',
                          cursor: 'pointer', fontSize: '14px', fontWeight: 500,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        <Trash2 size={14} /> Smazat šablonu
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          disabled
                          title="Šablony se nemažou — historické záznamy jsou povinné"
                          aria-disabled="true"
                          style={{
                            width: '100%', padding: '10px',
                            backgroundColor: '#F5F5F5', color: '#BDBDBD',
                            border: '1px solid #EEEEEE', borderRadius: '8px',
                            cursor: 'not-allowed', fontSize: '14px', fontWeight: 500,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: '8px', opacity: 0.7
                          }}
                        >
                          <Trash2 size={14} /> Smazat šablonu
                        </button>
                        <p style={{ textAlign: 'center', fontSize: '11px', color: '#BDBDBD', margin: '5px 0 0' }}>
                          Šablony se nemažou. Historické záznamy jsou povinné.
                        </p>
                      </>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={closeEditModal}
                    style={{
                      flex: 1, padding: '10px',
                      border: '1px solid #E0E0E0', borderRadius: '8px',
                      backgroundColor: '#FFF', cursor: 'pointer',
                      fontSize: '14px', fontWeight: 500
                    }}
                  >
                    Zrušit
                  </button>
                  <button
                    type="submit"
                    disabled={saving || editForm.machine_types.length === 0}
                    style={{
                      flex: 1, padding: '10px',
                      backgroundColor: (saving || editForm.machine_types.length === 0) ? '#CCC' : '#E53935',
                      color: '#FFF', border: 'none', borderRadius: '8px',
                      cursor: (saving || editForm.machine_types.length === 0) ? 'not-allowed' : 'pointer',
                      fontSize: '14px', fontWeight: 600
                    }}
                  >
                    {saving ? 'Ukládám...' : 'Uložit změny'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* ══════════════════════════════════════════════
            MODAL: Nahrát novou verzi souboru
        ══════════════════════════════════════════════ */}
        {showUpdateFor && createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="update-modal-title"
            style={{
              position: 'fixed', inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 9000
            }}
            onClick={(e) => { if (e.target === e.currentTarget) setShowUpdateFor(null); }}
          >
            <div style={{
              backgroundColor: '#FFF', borderRadius: '16px', padding: '24px',
              width: '100%', maxWidth: '400px', margin: '0 16px',
              boxShadow: '0 20px 25px rgba(0,0,0,0.15)'
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: '16px'
              }}>
                <h2 id="update-modal-title" style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>
                  Nahrát novou verzi
                </h2>
                <button
                  onClick={() => setShowUpdateFor(null)}
                  aria-label="Zavřít dialog"
                  style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <X size={20} color="#999" />
                </button>
              </div>

              <p style={{ color: '#666', marginBottom: '16px' }}>
                <strong>{showUpdateFor.name}</strong><br />
                <span style={{ fontSize: '14px', color: '#999' }}>
                  Aktuální: v{showUpdateFor.current_version || 1}
                </span>
              </p>

              <form onSubmit={handleUpdateVersion}>
                <div style={{ marginBottom: '16px' }}>
                  <label
                    htmlFor="update-file"
                    style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}
                  >
                    Nový soubor *
                  </label>
                  <input
                    id="update-file"
                    type="file"
                    accept=".docx,.pdf,.doc,.xlsx,.csv,.txt,.odt,.pptx,.jpg,.jpeg,.png"
                    onChange={(e) => setUploadForm({ file: e.target.files[0] })}
                    required
                    aria-required="true"
                    style={{
                      width: '100%', padding: '10px',
                      border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px'
                    }}
                  />
                </div>

                <p style={{ color: '#999', fontSize: '13px', margin: '0 0 16px' }}>
                  Stará verze zůstane zachována pro existující trenažéry.
                </p>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setShowUpdateFor(null)}
                    style={{
                      flex: 1, padding: '10px',
                      border: '1px solid #E0E0E0', borderRadius: '8px',
                      backgroundColor: '#FFF', cursor: 'pointer',
                      fontSize: '14px', fontWeight: 500
                    }}
                  >
                    Zrušit
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 1, padding: '10px',
                      backgroundColor: '#E53935', color: '#FFF',
                      border: 'none', borderRadius: '8px',
                      cursor: 'pointer', fontSize: '14px', fontWeight: 600
                    }}
                  >
                    Aktualizovat
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* Version history modal */}
        <TemplateVersionsModal
          key={showVersionsFor?.id || 'none'}
          show={!!showVersionsFor}
          template={showVersionsFor}
          onClose={() => setShowVersionsFor(null)}
        />

        {/* Confirm modal (for version update) */}
        <ConfirmModal
          show={!!confirmAction}
          title={confirmAction?.title}
          message={confirmAction?.message}
          confirmText="Potvrdit"
          cancelText="Zrušit"
          onConfirm={confirmAction?.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />

        {/* Toast notifications */}
        <ToastContainer toasts={toasts} onRemove={removeToast} />
      </div>
    </FadeIn>
  );
};

export default Templates;
